// Setup › Metrics & targets (`/c/{slug}/setup/metrics`, `kpi`; design spec §9; coverage rows
// 39–48, 26, 41). Four views in the URL (`?view=`): the metric definitions (new, edit, archive),
// the target rules (new, edit, delete), the seats' metric sets and weights (`?seat=`), and the
// seeded template catalog (add a template metric to the company). In the add-on's grace period
// everything reads and nothing writes. On load: the company (its currency), metrics and seats.
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  SearchField,
  SectionHeading,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpenIcon,
  PencilIcon,
  PlusIcon,
  RotateCcwIcon,
  RulerIcon,
  ScaleIcon,
  TargetIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef, MetricTemplate } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatMetricValue, humanize, TRACKING_LABELS } from "../format";
import { companyQuery, keys, metricsQuery, metricTemplatesQuery, seatsQuery } from "../queries";
import { Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { useFeature } from "../use-feature";
import { CADENCE_OPTIONS, labelOf, SOURCE_LABELS, UNIT_OPTIONS } from "./labels";
import { MetricDialog } from "./metric-dialog";
import { ConfirmButton, ErrorNotice, FormError, MoreRows, ReadOnlyNotice } from "./parts";
import { SeatMetricsEditor } from "./seat-metrics";
import { TargetsView } from "./targets";

export const METRICS_VIEWS = ["metrics", "targets", "weights", "templates"] as const;
export type MetricsView = (typeof METRICS_VIEWS)[number];

export function MetricsScreen({
  api,
  company,
  view,
  seatId,
  onView,
  onSeat,
}: {
  api: Api;
  company: CompanyAccess;
  view: MetricsView;
  seatId: string | undefined;
  onView: (view: MetricsView) => void;
  onSeat: (id: string | undefined) => void;
}) {
  const kpi = useFeature("kpi");
  const readOnly = kpi.readOnly;
  const agency = company.roles.includes("agency_operator");
  const detail = useQuery(companyQuery(api, company.id));
  const metrics = useInfiniteQuery(metricsQuery(api, company.id));
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  const [dialog, setDialog] = useState<"new-metric" | "new-target" | null>(null);
  const [editing, setEditing] = useState<MetricDef | null>(null);
  // Every page of the company's metrics: the target and seat-metric pickers, the key check and
  // the count read `all`, and a new metric (the API lists oldest first) lands on the last page.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = metrics;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const all = metrics.data?.pages.flatMap((p) => p.data) ?? [];
  const seatList = seats.data?.pages.flatMap((p) => p.data) ?? [];
  const currency = detail.data?.reporting_currency ?? "USD";
  const keysInUse = new Set(all.map((m) => m.key));

  const primary =
    view === "metrics" || view === "templates"
      ? { label: "New metric", run: () => setDialog("new-metric") }
      : view === "targets"
        ? { label: "New target", run: () => setDialog("new-target") }
        : null;

  return (
    <SetupPage
      title="Metrics & targets"
      width="full"
      {...(metrics.data ? { count: all.filter((m) => !m.archived_at).length } : {})}
      actions={
        primary && !readOnly ? (
          <Button size="sm" onClick={primary.run} disabled={!metrics.data}>
            <PlusIcon data-icon="inline-start" />
            {primary.label}
          </Button>
        ) : null
      }
      toolbar={
        <div className="min-w-0 strip-scroll border-b border-subtle px-6 max-md:px-4">
          <ViewTabs
            aria-label="Metrics views"
            value={view}
            onValueChange={(v) => onView(v as MetricsView)}
            tabs={[
              { value: "metrics", label: "Metrics", icon: RulerIcon },
              { value: "targets", label: "Targets", icon: TargetIcon },
              { value: "weights", label: "Seat weights", icon: ScaleIcon },
              { value: "templates", label: "Templates", icon: BookOpenIcon },
            ]}
          />
        </div>
      }
    >
      <div className="flex min-w-0 flex-col gap-4 px-6 py-4 max-md:px-4">
        {readOnly ? <ReadOnlyNotice what="metrics, targets and weights" /> : null}
        {metrics.isPending ? (
          <Loading label="Loading metrics" rows={5} />
        ) : metrics.isError ? (
          <ErrorNotice what="Metrics" error={metrics.error} />
        ) : view === "metrics" ? (
          <MetricsTable
            api={api}
            co={company.id}
            rows={all}
            readOnly={readOnly}
            onEdit={setEditing}
            onNew={() => setDialog("new-metric")}
            more={metrics}
          />
        ) : view === "targets" ? (
          <TargetsView
            api={api}
            co={company.id}
            metrics={all}
            seats={seatList}
            currency={currency}
            readOnly={readOnly}
            creating={dialog === "new-target"}
            onCreating={(open) => setDialog(open ? "new-target" : null)}
          />
        ) : view === "weights" ? (
          <SeatWeights
            api={api}
            company={company}
            seats={seatList.filter((s) => s.status !== "closed")}
            seatId={seatId}
            onSeat={onSeat}
            readOnly={readOnly}
            loading={seats.isPending}
          />
        ) : (
          <Templates
            api={api}
            co={company.id}
            currency={currency}
            keysInUse={keysInUse}
            readOnly={readOnly}
          />
        )}
      </div>
      {dialog === "new-metric" ? (
        <MetricDialog
          api={api}
          co={company.id}
          existingKeys={keysInUse}
          metrics={all}
          agency={agency}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {editing ? (
        <MetricDialog
          key={editing.id}
          api={api}
          co={company.id}
          metric={editing}
          existingKeys={keysInUse}
          metrics={all}
          agency={agency}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </SetupPage>
  );
}

type MetricFilter = "active" | "archived" | "all";

function MetricsTable({
  api,
  co,
  rows,
  readOnly,
  onEdit,
  onNew,
  more,
}: {
  api: Api;
  co: string;
  rows: MetricDef[];
  readOnly: boolean;
  onEdit: (m: MetricDef) => void;
  onNew: () => void;
  more: Parameters<typeof MoreRows>[0]["q"];
}) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<MetricFilter>("active");
  const [search, setSearch] = useState("");
  const archive = useMutation({
    mutationFn: (id: string) => api.client.metrics.archive({ params: { co, id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.metrics(co) }),
  });
  // ACTIONS: an archived metric live again (`POST metrics/{id}/unarchive`).
  const restore = useMutation({
    mutationFn: (id: string) => api.client.metrics.unarchive({ params: { co, id } }),
    onSuccess: (m) => {
      void qc.invalidateQueries({ queryKey: keys.metrics(co) });
      toast.add({ title: `${m.name} restored`, type: "success" });
    },
  });
  const term = search.trim().toLowerCase();
  const shown = rows.filter(
    (m) =>
      (filter === "all" || (filter === "archived") === Boolean(m.archived_at)) &&
      (!term || m.name.toLowerCase().includes(term) || m.key.includes(term)),
  );
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={RulerIcon}
        title="No metrics yet"
        actions={readOnly ? undefined : <Button onClick={onNew}>New metric</Button>}
      />
    );
  return (
    <div className="flex min-w-0 flex-col">
      <Toolbar className="mb-3">
        <Segmented
          aria-label="Metric status"
          size="sm"
          value={filter}
          onValueChange={setFilter}
          options={[
            { value: "active", label: "Active" },
            { value: "archived", label: "Archived" },
            { value: "all", label: "All" },
          ]}
        />
        <SearchField
          aria-label="Search metrics"
          placeholder="Search metrics…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>
      <Table data-slot="setup-metrics" className={DENSE_TABLE}>
        <TableCaption className="sr-only">Metric definitions of the company</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Metric</TableHead>
            <TableHead scope="col" className="max-md:hidden">
              Key
            </TableHead>
            <TableHead scope="col">Unit</TableHead>
            <TableHead scope="col" className="max-md:hidden">
              Source
            </TableHead>
            <TableHead scope="col" className="max-lg:hidden">
              Cadence
            </TableHead>
            <TableHead scope="col" className="max-lg:hidden">
              Visibility
            </TableHead>
            {readOnly ? null : (
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.length ? (
            shown.map((m) => (
              <TableRow key={m.id} data-metric={m.key} className="group/row">
                <TableHead scope="row">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {m.name}
                    {m.archived_at ? <Badge variant="outline">Archived</Badge> : null}
                  </span>
                </TableHead>
                <TableCell className="font-mono text-xs max-md:hidden">{m.key}</TableCell>
                <TableCell>{labelOf(UNIT_OPTIONS, m.unit)}</TableCell>
                <TableCell className="max-md:hidden">{SOURCE_LABELS[m.source]}</TableCell>
                <TableCell className="max-lg:hidden">
                  {m.cadence ? labelOf(CADENCE_OPTIONS, m.cadence) : "—"}
                </TableCell>
                <TableCell className="max-lg:hidden">
                  <Badge variant={m.visibility === "agency" ? "accent" : "neutral"}>
                    {m.visibility === "agency" ? "Agency" : "Client"}
                  </Badge>
                </TableCell>
                {readOnly ? null : (
                  <TableCell className="whitespace-nowrap py-1 text-right">
                    {m.archived_at ? (
                      <RowActions>
                        <RowAction
                          icon={RotateCcwIcon}
                          label={`Restore ${m.name}`}
                          tip="Restore"
                          disabled={restore.isPending}
                          onClick={() => restore.mutate(m.id)}
                        />
                      </RowActions>
                    ) : (
                      <RowActions>
                        <RowAction
                          icon={PencilIcon}
                          label={`Edit ${m.name}`}
                          onClick={() => onEdit(m)}
                        />
                        <ConfirmButton
                          label="Archive"
                          srLabel={m.name}
                          variant="ghost"
                          title={`Archive ${m.name}?`}
                          description="Its data stays and published scorecards keep it, but it can no longer be added to seats or receive values. Restore it from Archived."
                          confirmLabel="Archive metric"
                          pending={archive.isPending}
                          onConfirm={() => archive.mutateAsync(m.id)}
                        />
                      </RowActions>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={readOnly ? 6 : 7} className="text-muted-foreground">
                No metric matches.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <MoreRows what="metrics" q={more} />
    </div>
  );
}

function SeatWeights({
  api,
  company,
  seats,
  seatId,
  onSeat,
  readOnly,
  loading,
}: {
  api: Api;
  company: CompanyAccess;
  seats: { id: string; title: string }[];
  seatId: string | undefined;
  onSeat: (id: string | undefined) => void;
  readOnly: boolean;
  loading: boolean;
}) {
  if (loading) return <Loading label="Loading seats" rows={2} />;
  if (!seats.length)
    return (
      <EmptyState
        variant="card"
        icon={ScaleIcon}
        title="No open seats"
        description="Weights belong to a seat: create one under Setup › Seats first."
      />
    );
  const current = seats.find((s) => s.id === seatId);
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <Field className="max-w-md">
        <FieldLabel htmlFor="weights-seat">Seat</FieldLabel>
        <NativeSelect
          id="weights-seat"
          value={current?.id ?? ""}
          onChange={(e) => onSeat(e.target.value || undefined)}
        >
          <NativeSelectOption value="">Choose a seat…</NativeSelectOption>
          {seats.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {s.title}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      {current ? (
        <section aria-labelledby="weights-heading">
          <SectionHeading
            headingId="weights-heading"
            title={`${current.title}: metrics and weights`}
          />
          <SeatMetricsEditor
            key={current.id}
            api={api}
            company={company}
            seatId={current.id}
            readOnly={readOnly}
          />
        </section>
      ) : null}
    </div>
  );
}

function Templates({
  api,
  co,
  currency,
  keysInUse,
  readOnly,
}: {
  api: Api;
  co: string;
  currency: string;
  keysInUse: ReadonlySet<string>;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery(metricTemplatesQuery(api, co));
  const add = useMutation({
    mutationFn: (t: MetricTemplate) =>
      api.client.metrics.create({
        params: { co },
        body: {
          key: t.key,
          name: t.name,
          unit: t.unit,
          direction: t.direction,
          aggregation: t.aggregation,
          cadence: t.cadence,
          source: t.source,
          event_query: t.event_query,
          formula: t.formula,
          visibility: t.visibility,
        },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.metrics(co) }),
  });
  if (q.isPending) return <Loading label="Loading templates" rows={4} />;
  if (q.isError) return <ErrorNotice what="Templates" error={q.error} />;
  if (!q.data.data.length)
    return <EmptyState variant="card" icon={BookOpenIcon} title="No templates" />;
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <FormError what="Adding the metric" error={add.error} />
      {q.data.data.map((set) => (
        <section key={set.template_key} aria-labelledby={`tpl-${set.template_key}`}>
          <SectionHeading
            headingId={`tpl-${set.template_key}`}
            title={humanize(set.template_key)}
            count={set.metrics.length}
          />
          <Table data-slot="metric-templates" className={DENSE_TABLE}>
            <TableCaption className="sr-only">Template metrics</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Metric</TableHead>
                <TableHead scope="col">Unit</TableHead>
                <TableHead scope="col">Source</TableHead>
                <TableHead scope="col">Tracking</TableHead>
                <TableHead scope="col" numeric>
                  KPI weight
                </TableHead>
                <TableHead scope="col" numeric>
                  Weekly target
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Status</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {set.metrics.map((t) => (
                <TableRow key={t.key} data-template-metric={t.key}>
                  <TableHead scope="row">
                    <span className="block">{t.name}</span>
                    <span className="block font-mono text-xs font-normal text-muted-foreground">
                      {t.key}
                    </span>
                  </TableHead>
                  <TableCell>{labelOf(UNIT_OPTIONS, t.unit)}</TableCell>
                  <TableCell>{SOURCE_LABELS[t.source]}</TableCell>
                  <TableCell>{TRACKING_LABELS[t.tracking_mode]}</TableCell>
                  <TableCell numeric>{t.kpi_weight}</TableCell>
                  <TableCell numeric>
                    {t.default_weekly_target === null
                      ? "—"
                      : formatMetricValue(t.default_weekly_target, t.unit, currency)}
                  </TableCell>
                  <TableCell className="text-right">
                    {keysInUse.has(t.key) ? (
                      <Badge variant="success">Defined</Badge>
                    ) : readOnly ? null : (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={add.isPending}
                        onClick={() => add.mutate(t)}
                      >
                        Add<span className="sr-only"> {t.name}</span>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ))}
    </div>
  );
}
